import { differenceInMilliseconds, startOfDay } from "date-fns";
import { groupBy } from "lodash";
import React, { useCallback, useState } from "react";
import { ThemeProvider } from "styled-components";
import { TimesheetEntry } from "../../core/types";
import useCalendar from "../../hooks/use-calendar";
import { useNavigation } from "../../hooks/use-navigation";
import useTimesheet from "../../hooks/use-timesheet";
import theme from "../../theme";
import AppMenu from "../AppMenu";
import Calendar from "../Calendar";
import Debugger from "../Debugger";
import EntryDialog from "../EntryDialog";
import Layout from "../Layout";
import Pane from "../Pane";
import ShareDialog from "../ShareDialog";
import SheetsDialog from "../SheetsDialog";

const App: React.FC = () => {
  const ts = useTimesheet();
  const nav = useNavigation();

  const [editedEntry, setEditedEntry] = useState<TimesheetEntry | undefined>(
    undefined
  );

  const {
    currentWeekData,
    nextMonth,
    prevMonth,
    setSelectedDate,
    selectedDate,
  } = useCalendar();

  const handleSubmit = (start: Date, end: Date, description?: string) => {
    const duration = differenceInMilliseconds(end, start);
    ts.createEntry({ start, duration, description });
    setSelectedDate(startOfDay(start));
    nav.go("HOME");
  };

  const saveEntry = useCallback(
    (start: Date, end: Date, description?: string) => {
      const duration = differenceInMilliseconds(end, start);
      ts.updateEntry(editedEntry!, { start, duration, description });
      setEditedEntry(undefined);
    },
    [editedEntry]
  );

  return (
    <ThemeProvider theme={theme}>
      <Layout
        top={
          <div>
            <div id="menu">
              <AppMenu
                sheetName={ts.activeSheet}
                onChangeSheet={() => nav.go("SHEETS")}
                onToggleShareDialog={() => nav.go("SHARE")}
              />
              {nav.view === "SHARE" && (
                <ShareDialog
                  latestImport={ts.latestImport}
                  entries={ts.entries}
                  sheetName={ts.activeSheet}
                  onClosed={() => nav.go("HOME")}
                  show={nav.view === "SHARE"}
                  onImport={(entries, options) => ts.import(entries, options)}
                />
              )}

              {nav.view === "SHEETS" && (
                <SheetsDialog
                  sheets={ts.sheets}
                  show={nav.view === "SHEETS"}
                  onCreateSheet={(sheetName) => ts.createSheet(sheetName)}
                  onRemoveSheet={(sheetName) => ts.removeSheet(sheetName)}
                  onChangeSheet={(sheetName) => {
                    ts.changeSheet(sheetName);
                    nav.go("HOME");
                  }}
                  onRenameSheet={ts.renameSheet}
                  onClose={() => nav.go("HOME")}
                />
              )}

              {nav.view === "EDITOR" && (
                <EntryDialog
                  isOpen={nav.view === "EDITOR"}
                  entries={ts.entries}
                  onClose={() => nav.go("HOME")}
                  onSubmit={handleSubmit}
                  day={selectedDate}
                />
              )}

              {editedEntry && (
                <EntryDialog
                  entry={editedEntry}
                  isOpen
                  onClose={() => setEditedEntry(undefined)}
                  onSubmit={saveEntry}
                  day={startOfDay(editedEntry.start)}
                  onRemove={() => {
                    ts.removeEntry(editedEntry);
                    setEditedEntry(undefined);
                  }}
                />
              )}
            </div>
            <Calendar
              timesheetEntries={ts.entries}
              selectedDate={selectedDate}
              currentWeekData={currentWeekData}
              onNextMonth={nextMonth}
              onPrevMonth={prevMonth}
              onSelectedDate={setSelectedDate}
            />
          </div>
        }
        bottom={
          <div>
            <Pane
              timesheetEntries={groupBy(
                ts.entries.filter(
                  (e) => e.start.getMonth() === selectedDate.getMonth()
                ),
                (e) => startOfDay(e.start).toISOString()
              )}
              selectedDate={selectedDate}
              onEditEntry={setEditedEntry}
              onSelectDay={setSelectedDate}
              onCreate={(date) => {
                setSelectedDate(date);
                nav.go("EDITOR");
              }}
              onRemoveAll={(entries) => {
                entries.forEach(ts.removeEntry);
              }}
              listenToScroll={nav.view === "HOME"}
            />
          </div>
        }
      />
      <Debugger
        onCreateEntries={(entries) => entries.forEach(ts.createEntry)}
        onClear={() => ts.entries.forEach(ts.removeEntry)}
        selectedDate={selectedDate}
      />
    </ThemeProvider>
  );
};

export default App;
